/**
 * UNIFIED AI SERVICE - Multi-Provider Support
 * 
 * Supports: OpenAI, Google Gemini, Anthropic Claude
 * Features: Encrypted key storage, automatic fallback, usage tracking
 * 
 * USAGE RULES:
 * - AI only runs on explicit user action (button click)
 * - Never runs automatically
 * - Provides suggestions, user can edit before saving
 * - Falls back gracefully if AI unavailable
 */

import { supabase } from '../lib/supabase';
import CryptoJS from 'crypto-js';

// Encryption key (in production, use environment variable)
const ENCRYPTION_KEY = import.meta.env?.VITE_ENCRYPTION_KEY || 'default-encryption-key-change-in-production';

/**
 * Encrypt API key before storage
 */
function encryptKey(apiKey) {
  return CryptoJS?.AES?.encrypt(apiKey, ENCRYPTION_KEY)?.toString();
}

/**
 * Decrypt API key from storage
 */
function decryptKey(encryptedKey) {
  const bytes = CryptoJS?.AES?.decrypt(encryptedKey, ENCRYPTION_KEY);
  return bytes?.toString(CryptoJS?.enc?.Utf8);
}

/**
 * Get AI configuration from encrypted storage
 */
export async function getAIConfig() {
  try {
    const { data, error } = await supabase
      ?.from('ai_api_keys_encrypted')
      ?.select('*');

    if (error) throw error;

    // Convert to config format (without decrypting keys)
    const config = {};
    data?.forEach(row => {
      config[row?.provider] = {
        enabled: row?.enabled,
        model: row?.model,
        usageLimit: row?.usage_limit,
        connectionStatus: row?.connection_status,
        lastTestedAt: row?.last_tested_at,
        responseTime: row?.response_time,
        hasKey: !!row?.encrypted_key
      };
    });

    return config;
  } catch (error) {
    console.error('Error fetching AI config:', error);
    return {};
  }
}

/**
 * Save encrypted API key
 */
export async function saveAPIKey(provider, apiKey, config = {}) {
  try {
    const encryptedKey = encryptKey(apiKey);

    const { data, error } = await supabase
      ?.from('ai_api_keys_encrypted')
      ?.upsert({
        provider,
        encrypted_key: encryptedKey,
        enabled: config?.enabled ?? true,
        model: config?.model,
        usage_limit: config?.usageLimit || 1000,
        connection_status: 'not_tested'
      }, { onConflict: 'provider' })
      ?.select()
      ?.single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error saving API key:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Delete API key from encrypted storage
 */
export async function deleteAPIKey(provider) {
  try {
    const { error } = await supabase
      ?.from('ai_api_keys_encrypted')
      ?.delete()
      ?.eq('provider', provider);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting API key:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get decrypted API key from storage
 */
export async function getDecryptedKey(provider) {
  try {
    const { data, error } = await supabase
      ?.from('ai_api_keys_encrypted')
      ?.select('encrypted_key')
      ?.eq('provider', provider)
      ?.single();

    if (error || !data?.encrypted_key) return null;
    return decryptKey(data?.encrypted_key);
  } catch (error) {
    console.error(`Error getting decrypted key for ${provider}:`, error);
    return null;
  }
}

/**
 * Test connection to AI provider
 */
export async function testConnection(provider, apiKey) {
  try {
    const startTime = Date.now();
    let success = false;
    let message = '';

    switch (provider) {
      case 'openai': {
        const response = await fetch('https://api.openai.com/v1/models', {
          headers: { 'Authorization': `Bearer ${apiKey}` }
        });
        success = response?.ok;
        if (!success) {
          const errorData = await response?.json();
          message = errorData?.error?.message || 'Falha na conexão';
        }
        break;
      }

      case 'gemini': {
        const response = await fetch(
          `https://generativelanguage.googleapis.com/v1/models?key=${apiKey}`
        );
        success = response?.ok;
        if (!success) {
          const errorData = await response?.json();
          message = errorData?.error?.message || 'Falha na conexão';
        }
        break;
      }

      case 'anthropic': {
        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'content-type': 'application/json'
          },
          body: JSON.stringify({
            model: 'claude-3-haiku-20240307',
            max_tokens: 10,
            messages: [{ role: 'user', content: 'test' }]
          })
        });
        success = response?.ok;
        if (!success) {
          const errorData = await response?.json();
          message = errorData?.error?.message || 'Falha na conexão';
        }
        break;
      }

      case 'groq': {
        const response = await fetch('https://api.groq.com/openai/v1/models', {
          headers: { 'Authorization': `Bearer ${apiKey}` }
        });
        success = response?.ok;
        if (!success) {
          const errorData = await response?.json();
          message = errorData?.error?.message || 'Falha na conexão';
        }
        break;
      }

      default:
        return { success: false, message: 'Provedor não suportado' };
    }

    const responseTime = Date.now() - startTime;

    // Update connection status in database
    await supabase
      ?.from('ai_api_keys_encrypted')
      ?.update({
        connection_status: success ? 'connected' : 'failed',
        last_tested_at: new Date()?.toISOString(),
        response_time: responseTime
      })
      ?.eq('provider', provider);

    return {
      success,
      responseTime,
      message: success ? 'Conexão bem-sucedida' : message
    };
  } catch (error) {
    console.error(`Error testing ${provider}:`, error);
    return {
      success: false,
      responseTime: 0,
      message: error?.message || 'Erro ao testar conexão'
    };
  }
}

/**
 * Generate AI content using the first available provider
 * Falls back to environment variables if database config not available
 */
export async function generateContent(prompt, options = {}) {
  try {
    // Try database-configured providers first
    const config = await getAIConfig();
    const enabledProviders = Object.entries(config)?.filter(([_, cfg]) => cfg?.enabled && cfg?.hasKey)?.map(([provider]) => provider);

    // If no database providers, try environment variables
    if (enabledProviders?.length === 0) {
      console.log('No database providers configured, trying environment variables...');
      
      // Try OpenAI from environment
      if (import.meta.env?.VITE_OPENAI_API_KEY) {
        try {
          let result = await generateWithOpenAI(prompt, {
            apiKey: import.meta.env?.VITE_OPENAI_API_KEY,
            ...options
          });
          return { success: true, content: result };
        } catch (error) {
          console.error('OpenAI (env) failed:', error?.message);
        }
      }
      
      // Try Gemini from environment
      if (import.meta.env?.VITE_GEMINI_API_KEY && import.meta.env?.VITE_GEMINI_API_KEY !== 'your-gemini-api-key-here') {
        try {
          let result = await generateWithGemini(prompt, {
            apiKey: import.meta.env?.VITE_GEMINI_API_KEY,
            ...options
          });
          return { success: true, content: result };
        } catch (error) {
          console.error('Gemini (env) failed:', error?.message);
        }
      }
      
      // Try Anthropic from environment
      if (import.meta.env?.VITE_ANTHROPIC_API_KEY && import.meta.env?.VITE_ANTHROPIC_API_KEY !== 'your-anthropic-api-key-here') {
        try {
          let result = await generateWithAnthropic(prompt, {
            apiKey: import.meta.env?.VITE_ANTHROPIC_API_KEY,
            ...options
          });
          return { success: true, content: result };
        } catch (error) {
          console.error('Anthropic (env) failed:', error?.message);
        }
      }
      
      // Try Groq from environment
      if (import.meta.env?.VITE_GROQ_API_KEY && import.meta.env?.VITE_GROQ_API_KEY !== 'your-groq-api-key-here') {
        try {
          let result = await generateWithGroq(prompt, {
            apiKey: import.meta.env?.VITE_GROQ_API_KEY,
            ...options
          });
          return { success: true, content: result };
        } catch (error) {
          console.error('Groq (env) failed:', error?.message);
        }
      }
      
      return { 
        success: false, 
        error: 'IA não disponível. Configure pelo menos uma chave de API em AI Assistant Hub ou no arquivo .env' 
      };
    }

    // Try each enabled provider from database
    const errors = [];
    for (const provider of enabledProviders) {
      try {
        const apiKey = await getDecryptedKey(provider);
        if (!apiKey) {
          errors?.push(`${provider}: Chave de API não encontrada`);
          continue;
        }

        let result;
        switch (provider) {
          case 'openai':
            result = await generateWithOpenAI(prompt, { apiKey, ...options });
            break;
          case 'gemini':
            result = await generateWithGemini(prompt, { apiKey, ...options });
            break;
          case 'anthropic':
            result = await generateWithAnthropic(prompt, { apiKey, ...options });
            break;
          case 'groq':
            result = await generateWithGroq(prompt, { apiKey, ...options });
            break;
          default:
            throw new Error(`Provedor desconhecido: ${provider}`);
        }

        // Track successful usage
        await trackUsage(provider, prompt?.length, result?.length);
        return { success: true, content: result };
      } catch (error) {
        console.error(`${provider} failed:`, error?.message);
        errors?.push(`${provider}: ${error?.message}`);
        continue;
      }
    }

    return { 
      success: false, 
      error: `Todos os provedores de IA falharam:\n${errors?.join('\n')}` 
    };
  } catch (error) {
    console.error('AI generation error:', error);
    return { success: false, error: error?.message || 'Erro ao gerar conteúdo com IA' };
  }
}

/**
 * Call specific AI provider
 */
async function callAIProvider(provider, apiKey, prompt, options) {
  const model = options?.model || getDefaultModel(provider);

  switch (provider) {
    case 'openai':
      return await callOpenAI(apiKey, model, prompt, options);
    case 'gemini':
      return await callGemini(apiKey, model, prompt, options);
    case 'anthropic':
      return await callAnthropic(apiKey, model, prompt, options);
    case 'groq':
      return await callGroq(apiKey, model, prompt, options);
    default:
      throw new Error(`Provedor não suportado: ${provider}`);
  }
}

function getDefaultModel(provider) {
  const defaults = {
    openai: 'gpt-4o-mini',
    gemini: 'gemini-1.5-flash-latest',
    anthropic: 'claude-3-haiku-20240307',
    groq: 'llama-3.3-70b-versatile'
  };
  return defaults?.[provider];
}

/**
 * OpenAI API call
 */
async function callOpenAI(apiKey, model, prompt, options) {
  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      model,
      messages: [{ role: 'user', content: prompt }],
      max_tokens: options?.maxTokens || 1000,
      temperature: options?.temperature || 0.7
    })
  });

  if (!response?.ok) {
    const error = await response?.json();
    throw new Error(error?.error?.message || 'OpenAI API error');
  }

  const data = await response?.json();
  return {
    success: true,
    content: data?.choices?.[0]?.message?.content,
    provider: 'openai',
    model
  };
}

/**
 * Google Gemini API call
 */
async function callGemini(apiKey, model, prompt, options) {
  const response = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          maxOutputTokens: options?.maxTokens || 1000,
          temperature: options?.temperature || 0.7
        }
      })
    }
  );

  if (!response?.ok) {
    const error = await response?.json();
    throw new Error(error?.error?.message || 'Gemini API error');
  }

  const data = await response?.json();
  return {
    success: true,
    content: data?.candidates?.[0]?.content?.parts?.[0]?.text,
    provider: 'gemini',
    model
  };
}

/**
 * Anthropic Claude API call
 */
async function callAnthropic(apiKey, model, prompt, options) {
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'content-type': 'application/json'
    },
    body: JSON.stringify({
      model,
      max_tokens: options?.maxTokens || 1000,
      messages: [{ role: 'user', content: prompt }]
    })
  });

  if (!response?.ok) {
    const error = await response?.json();
    throw new Error(error?.error?.message || 'Anthropic API error');
  }

  const data = await response?.json();
  return {
    success: true,
    content: data?.content?.[0]?.text,
    provider: 'anthropic',
    model
  };
}

/**
 * Groq API call
 */
async function callGroq(apiKey, model, prompt, options) {
  const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      model,
      messages: [{ role: 'user', content: prompt }],
      max_tokens: options?.maxTokens || 1000,
      temperature: options?.temperature || 0.7
    })
  });

  if (!response?.ok) {
    const error = await response?.json();
    throw new Error(error?.error?.message || 'Groq API error');
  }

  const data = await response?.json();
  return {
    success: true,
    content: data?.choices?.[0]?.message?.content,
    provider: 'groq',
    model
  };
}

/**
 * Generate content with OpenAI
 */
async function generateWithOpenAI(prompt, options = {}) {
  const apiKey = options?.apiKey;
  const model = options?.model || 'gpt-4o-mini';
  let result = await callOpenAI(apiKey, model, prompt, options);
  return result?.content;
}

/**
 * Generate content with Gemini
 */
async function generateWithGemini(prompt, options = {}) {
  const apiKey = options?.apiKey;
  const model = options?.model || 'gemini-1.5-flash-latest';
  let result = await callGemini(apiKey, model, prompt, options);
  return result?.content;
}

/**
 * Generate content with Anthropic
 */
async function generateWithAnthropic(prompt, options = {}) {
  const apiKey = options?.apiKey;
  const model = options?.model || 'claude-3-haiku-20240307';
  let result = await callAnthropic(apiKey, model, prompt, options);
  return result?.content;
}

/**
 * Generate content with Groq
 */
async function generateWithGroq(prompt, options = {}) {
  const apiKey = options?.apiKey;
  const model = options?.model || 'llama-3.3-70b-versatile';
  let result = await callGroq(apiKey, model, prompt, options);
  return result?.content;
}

/**
 * Track AI usage
 */
async function trackUsage(provider, inputTokens, outputTokens) {
  try {
    await supabase
      ?.from('ai_usage_tracking')
      ?.insert({
        provider,
        input_tokens: inputTokens,
        output_tokens: outputTokens,
        timestamp: new Date()?.toISOString()
      });
  } catch (error) {
    console.error('Error tracking usage:', error);
  }
}

export const aiService = {
  getAIConfig,
  saveAPIKey,
  deleteAPIKey,
  testConnection,
  generateContent
};